import type { LpAllocationRow } from '@/types/lpAllocation';

export type LpAllocationDraft = Record<string, number | null>;

export interface LpAllocationErrors {
  total?: string;
  overCommitment?: string;
}

function toCents(amount: number): number {
  return Math.round(amount * 100);
}

export function draftFromRows(rows: LpAllocationRow[]): LpAllocationDraft {
  return Object.fromEntries(
    rows.map((row) => [row.investor_fund_relationship_id, row.amount])
  );
}

export function draftAmount(draft: LpAllocationDraft, row: LpAllocationRow) {
  return draft[row.investor_fund_relationship_id] ?? 0;
}

export function draftTotal(draft: LpAllocationDraft): number {
  return (
    Object.values(draft).reduce<number>(
      (cents, amount) => cents + toCents(amount ?? 0),
      0
    ) / 100
  );
}

export function addsUpToInvestment(
  draft: LpAllocationDraft,
  investedAmount: number
): boolean {
  return toCents(draftTotal(draft)) === toCents(investedAmount);
}

export function remainingCommitment(row: LpAllocationRow, amount: number) {
  return (
    (toCents(row.commitment_amount) -
      toCents(row.allocated_to_other_investments) -
      toCents(amount)) /
    100
  );
}

/** An LP left at zero is never over, even if other investments already overran them. */
export function isOverCommitment(row: LpAllocationRow, amount: number) {
  return amount > 0 && remainingCommitment(row, amount) < 0;
}

export function validateLpAllocation(
  rows: LpAllocationRow[],
  draft: LpAllocationDraft,
  investedAmount: number
): LpAllocationErrors {
  const errors: LpAllocationErrors = {};
  if (!addsUpToInvestment(draft, investedAmount)) {
    errors.total = 'Allocations must add up to the investment amount';
  }
  if (rows.some((row) => isOverCommitment(row, draftAmount(draft, row)))) {
    errors.overCommitment =
      'Some LPs are allocated more than their remaining commitment';
  }
  return errors;
}

export function hasAnyError(errors: LpAllocationErrors): boolean {
  return Object.keys(errors).length > 0;
}
